import React from "react";
import * as Ariakit from "@ariakit/react";
import styled from "styled-components";
import { XCircle } from "lucide-react";
import Button from "../../button/button";
import { Color } from "../../../constants/color";
import { ZIndex } from "../../../constants/z-index";

/*
 Conferma di un'operazione che non si torna indietro. Prende il posto di window.confirm,
 che parlava con i colori del sistema operativo e non diceva niente del contesto: qui il
 titolo e il testo restano quelli dell'applicazione, e il bottone che conferma e' quello
 che si e' appena premuto, con lo stesso colore.
 */

const Sfondo = styled.div`
  position: fixed;
  inset: 0;
  z-index: ${ZIndex.DIALOG};
  background-color: ${Color.SHADOW};
  opacity: 0.5;
`;

const Riquadro = styled(Ariakit.Dialog)`
  position: fixed;
  inset: 0;
  z-index: ${ZIndex.DIALOG};
  margin: auto;
  height: fit-content;
  width: min(32rem, calc(100dvw - 2rem));
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-style: hidden;
  border-radius: 1rem;
  background-color: ${Color.WHITE};
  box-shadow: 0 25px 50px -12px ${Color.SHADOW};
  font-family: "Avenir Next", sans-serif;

  /* La dialog vive in un portale, fuori dal reset di pagina: si regola da sola. */
  &,
  & * {
    box-sizing: border-box;
  }
`;

const Intestazione = styled(Ariakit.DialogHeading)`
  position: relative;
  margin: 0;
  padding: 0.6rem 2.5rem;
  background-color: ${Color.BLUE};
  color: ${Color.WHITE};
  font-size: 1.3rem;
  font-weight: bold;
  text-align: center;
`;

const Chiudi = styled(Ariakit.DialogDismiss)`
  position: absolute;
  top: calc(50% - 12px);
  right: 0.6rem;
  background-color: ${Color.NONE};
  border-style: hidden;
  padding: 0;
  cursor: pointer;
  line-height: 0;
`;

const Corpo = styled.p`
  margin: 0;
  padding: 1.4rem 1.6rem;
  color: ${Color.DARK_GREY};
  font-size: 1rem;
  line-height: 1.4;
  text-align: center;
`;

const Bottoni = styled.div`
  display: flex;
  justify-content: center;
  column-gap: 1.5rem;
  padding: 0 1.6rem 1.4rem;
`;

interface Props {
  aperta: boolean;
  titolo: string;
  messaggio: string;
  etichettaConferma: string;
  etichettaAnnulla?: string;
  /*
   I colori dei due bottoni sono regolabili perche' non tutte le conferme hanno lo stesso
   peso. Dove si conferma un'operazione ordinaria vale il blu sulla conferma; dove si
   conferma una distruzione, il rosso va sul bottone che distrugge e il blu su quello che
   riporta indietro, altrimenti il colore d'allarme finisce sulla via d'uscita.
  */
  coloreConferma?: string;
  coloreAnnulla?: string;
  onConferma: () => void;
  onAnnulla: () => void;
}

const DialogConferma = ({
  aperta,
  titolo,
  messaggio,
  etichettaConferma,
  etichettaAnnulla = "ANNULLA",
  coloreConferma = Color.BLUE,
  coloreAnnulla = Color.RED,
  onConferma,
  onAnnulla,
}: Props) => (
  <Riquadro open={aperta} onClose={onAnnulla} backdrop={<Sfondo />}>
    <Intestazione render={<h2 />}>
      {titolo}
      <Chiudi aria-label="Chiudi">
        <XCircle size={24} color={Color.WHITE} />
      </Chiudi>
    </Intestazione>
    <Corpo>{messaggio}</Corpo>
    <Bottoni>
      <Button
        label={etichettaConferma}
        color={coloreConferma}
        onClick={onConferma}
      />
      <Button
        label={etichettaAnnulla}
        color={coloreAnnulla}
        onClick={onAnnulla}
      />
    </Bottoni>
  </Riquadro>
);

export default DialogConferma;
